import React, { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { useRootStore } from "../../../../store/contexts";
import { useDataGridHandlers } from "../../../../common/hooks";
import {
  DataGrid,
  DataGridColumn,
  DataGridRowAction,
} from "../../../../common/controls/lists/DataGrid";
import { BotPhase } from "../../../../api/graphql";
import { BotPhaseListStore } from "../../store";

import { BotStepPanel } from "./BotStepPanel";
import { VisibilityIcon } from "../../../../icons";

export type GridExpressProps = { listStore?: BotPhaseListStore };

export const BotPhaseListForm: React.FC<GridExpressProps> = observer(
  ({ listStore }) => {
    const rootStore = useRootStore();
    const { langStore } = rootStore;

    if (!listStore) {
      return null;
    }

    const store = listStore;

    // Handlers de DataGrid (extraídos a hook común)
    const {
      handleColumnVisibilityChange,
      handleColumnOrderChange,
      handleColumnSizingChange,
      handleFiltersChange,
      handleSortingChange,
      handlePaginationChange,
    } = useDataGridHandlers(store, rootStore);

    // Row actions
    const rowActions: DataGridRowAction<BotPhase>[] = useMemo(
      () => [
        {
          label: langStore.viewCommand,
          onClick: (row: BotPhase, event?: React.MouseEvent) =>
            store.viewRow(row.id, event, "menu"),
          icon: <VisibilityIcon fontSize="small" />,
        },
      ],
      [langStore, store]
    );

    // Columnas del store con accessorFn para referencias
    const columns = useMemo(() => {
      const storeColumns = store.grid
        .dataGridColumns as unknown as DataGridColumn<BotPhase>[];
      return storeColumns.map((col) => {
        if (col.accessorKey === "startedAt") {
          return {
            ...col,
            id: "startedAt",
            accessorFn: (row: BotPhase) =>
              row.startedAt
                ? new Date(row.startedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "completedAt") {
          return {
            ...col,
            id: "completedAt",
            accessorFn: (row: BotPhase) =>
              row.completedAt
                ? new Date(row.completedAt).toLocaleString("es-ES", {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  })
                : "-",
          };
        }
        if (col.accessorKey === "steps") {
          return {
            ...col,
            id: "steps",
            accessorFn: (row: BotPhase) =>
              row.steps?.length ? `${row.steps.length} item(s)` : "-",
          };
        }
        return col;
      });
    }, [store.grid.dataGridColumns]);

    const rows = store.botPhaseCurrentPage || [];

    return (
      <Box id={"ListOfBotPhase"}>
        <DataGrid
          columns={columns}
          data={rows}
          isLoading={store.status === "fetching"}
          enableFiltering
          enableShowHideFilters
          onFiltersChange={handleFiltersChange}
          initialFilters={store.grid.filtering}
          sorting={store.grid.sorting.map((s) => ({
            id: s.columnName,
            desc: s.direction === "desc",
          }))}
          onSortingChange={handleSortingChange}
          pagination={{
            pageIndex: store.currentPage - 1,
            pageSize: store.pageSize,
          }}
          onPaginationChange={handlePaginationChange}
          rowCount={store.totalRows + (store.hasMorePages ? 1 : 0)}
          manualPagination
          enableColumnOrdering
          enableColumnResizing
          enableHiding
          columnVisibility={store.grid.mrtColumnVisibility}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          columnOrder={store.grid.columnOrder}
          onColumnOrderChange={handleColumnOrderChange}
          columnSizing={store.grid.mrtColumnSizing}
          onColumnSizingChange={handleColumnSizingChange}
          showColumnFilters={store.grid.showColumnFilters}
          onShowColumnFiltersChange={(value) =>
            store.grid.setShowColumnFilters(value)
          }
          isFullScreen={store.grid.isFullScreen}
          onIsFullScreenChange={(value) => store.grid.setIsFullScreen(value)}
          getRowId={(row) => row.id}
          language={langStore.language as "es" | "en" | "pt"}
          onRowClick={(row, event) => store.handleRowClick(row, event)}
          selectedRowId={store.selectedRowId}
          rowActions={rowActions}
          muiTableBodyRowProps={() => ({
            sx: { cursor: "pointer" },
          })}
          enableExpanding
          renderDetailPanel={({ row }) => (
            <Box sx={{ pl: 4, pr: 2, pb: 2, backgroundColor: "action.hover" }}>
              <BotStepPanel parentId={row.original.id} />
            </Box>
          )}
        />
      </Box>
    );
  }
);
